import { useCallback, useEffect, useState } from "react";

import { getToolCallIds } from "../chat.helpers";
import type { ExtendedUIMessage } from "../chat.types";
import { pruneHeldTurns } from "../ephemeral-slot";
import { normalizeLiveTurn } from "../render-identity";

export interface UseHeldResolvedTurnsResult {
  /** The render-only held turns, injected into the render base by the caller (dedupe collapses by renderKey). */
  heldResolvedTurns: ExtendedUIMessage[];
  /**
   * Park the turns resolving `toolCallId` into the holding area, tagged by `renderKey`. Returns the
   * parked turns so the caller can decide whether to also drop them from the `useChat` buffer (the
   * buffer is the caller's concern, not this owner's). A no-op — and an empty return — when no turn
   * in `messages` carries `toolCallId`.
   */
  holdTurnsResolvingElicit: (messages: ExtendedUIMessage[], toolCallId: string) => ExtendedUIMessage[];
}

/**
 * Sole owner of the B-1445 held-turns state — the render-only holding area for a resolved elicit turn.
 *
 * When an elicit resolve drops the settled turn from the `useChat` buffer for a clean resume seed, the
 * turn is parked here (keyed by `renderKey`) so the render keeps showing it through the
 * settle→history-refetch window — then released the moment its authoritative `serverHistory` row lands
 * (gap-free by `renderKey`). This makes the clean seed independent of refetch timing without ever
 * removing the only rendered copy.
 *
 * The state was previously coordinated by several independently-triggered effects/blocks in
 * `useMcpChat` (insertion in the submit path, a prune effect, a cross-chat clear in the
 * compare-previous-render block, and the render-base injection). Each B-1445 review pass found a
 * DIFFERENT missing/mismatched trigger in that lifecycle. Folding all three write paths into this one
 * owner makes the "missing trigger" class structurally impossible: every mutation converges here.
 *
 * Convergence rule: the holding area re-prunes on ANY change to `heldResolvedTurns` OR `serverHistory`
 * (matched by `renderKey`), so it releases a held turn whichever side arrives last — including the case
 * where the insertion parks a turn whose row is ALREADY in `serverHistory` (pruned on the very next
 * render off the held-state change, not only on a future `serverHistory` update that may never come if
 * the resume attach fails). `pruneHeldTurns` returns the same reference when nothing changes, so the
 * `heldResolvedTurns` dep can't loop.
 *
 * Cross-chat clear: held turns are cleared synchronously on a `chatKey` change (session switch) via the
 * React-canonical compare-previous-render pattern — so a stale cross-chat held bubble can never render
 * for a frame in the new chat (a `useEffect` reset would commit one frame first).
 */
export function useHeldResolvedTurns(params: {
  serverHistory: ExtendedUIMessage[];
  chatKey: string;
}): UseHeldResolvedTurnsResult {
  const { serverHistory, chatKey } = params;

  const [heldResolvedTurns, setHeldResolvedTurns] = useState<ExtendedUIMessage[]>([]);

  // Convergence: release a held turn once its authoritative row lands in `serverHistory` (matched by
  // `renderKey`). Keyed on BOTH `serverHistory` and `heldResolvedTurns` so the holding area converges
  // whichever side arrives last — see the class comment's convergence rule.
  useEffect(() => {
    setHeldResolvedTurns((held) => pruneHeldTurns(held, serverHistory));
  }, [serverHistory, heldResolvedTurns]);

  // Cross-chat clear on a session switch — synchronous compare-previous-render (no one-frame leak).
  const [prevChatKey, setPrevChatKey] = useState(chatKey);
  if (chatKey !== prevChatKey) {
    setPrevChatKey(chatKey);
    setHeldResolvedTurns((held) => (held.length === 0 ? held : []));
  }

  const holdTurnsResolvingElicit = useCallback(
    (messages: ExtendedUIMessage[], toolCallId: string): ExtendedUIMessage[] => {
      // Normalize each resolved turn to its canonical render form (own parts + `renderKey`) via the
      // SAME `normalizeLiveTurn` the render path uses. The `renderKey` makes the held copy and its
      // authoritative row ONE keyed element (gap-free collapse + prune release); the own-parts slice
      // (B-1454) is what keeps a parent→child-elicit held turn from carrying the parent's inherited
      // `executePlaybook` tool-call id — otherwise the shared-tool-call dedupe against the persisted
      // parent row would drop the held elicit and blank it during the hold window.
      const resolvedTurns = messages
        .filter((message) => getToolCallIds(message.parts).includes(toolCallId))
        .map(normalizeLiveTurn);
      if (resolvedTurns.length > 0) {
        setHeldResolvedTurns((held) => [...held, ...resolvedTurns]);
      }
      return resolvedTurns;
    },
    []
  );

  return { heldResolvedTurns, holdTurnsResolvingElicit };
}
